import {
  AccountInfo,
  AuthenticationResult,
  EndSessionRequest,
  PopupRequest,
  PublicClientApplication,
  RedirectRequest,
  SsoSilentRequest,
} from '@azure/msal-browser';
import type { AuthStateUpdate, SetGlobalDataCache } from 'App/AppContextProvider';
import { globalReturnUrls } from 'globalConstants';
import CurrentUser from 'models/users/currentuser';
import { IdentityProviderType } from 'models/identity/identityProvider';
import Tenant from 'models/tenants/tenant';
import User from 'models/users/user';
import { apiLoginUser } from 'services/Api/userService';
import { apiRequest, getMsalConfigForTenant, loginRequest, msalConfigCommon } from 'services/Auth/authConfig';
import Config from 'services/Config/configService';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import { LocalStorageKeys, getLocalStorageData, removeLocalStorageData, setLocalStorageData } from 'utils/localstorage';
import { AdminConsentParams } from './adminConsentParams';
import { isAuthInteractionError, isAuthUserError, isConsentRaceError, isPopupWindowError } from './authErrors';
import { completeLoginProfile } from './loginProfile';

//
// Silent login
//
const ssoSilent = async (
  publicClientApplication: PublicClientApplication,
  entraTenantId: string | undefined,
  authStateUpdate: AuthStateUpdate,
  setGlobalDataCache: SetGlobalDataCache,
  allowInteraction: boolean = true,
) => {
  try {
    const redirectHandled = await handleRedirectResponse(publicClientApplication, authStateUpdate, setGlobalDataCache);
    if (redirectHandled) {
      return;
    }

    logger.debug('Method: ssoSilent');

    //if tenant is provided, try to login to that tenant
    if (entraTenantId) {
      const azureTenant = new Tenant(entraTenantId, '');
      azureTenant.providerTenantId = entraTenantId;
      await switchTenant(publicClientApplication, authStateUpdate, setGlobalDataCache, azureTenant);

      return;
    }

    // If MSAL already has an account, the user
    // is already logged in, else try to do a silent SSO
    authStateUpdate(undefined, undefined, true, undefined);
    const accounts = publicClientApplication.getAllAccounts();

    if (accounts && accounts.length > 0) {
      if (accounts.length === 1) {
        logger.debug('Single account found', accounts[0]);
        // 1 account
        publicClientApplication.setActiveAccount(accounts[0]);
        await setUserProfile(publicClientApplication, authStateUpdate, setGlobalDataCache);
      } else {
        // multiple accounts
        // choose the local account from the storageKey, otherwise login
        logger.debug(
          'Multiple accounts found',
          accounts.map((a) => {
            return { tenantId: a.tenantId, username: a.username, localAccountId: a.localAccountId };
          }),
        );
        const localAccountId = getLocalAccountFromStorage();
        const localAccount = accounts.find((a) => a.localAccountId === localAccountId);
        logger.debug('Search for local account Id: ' + (localAccountId ?? 'unknown'), localAccount || 'not found');
        if (localAccount) {
          publicClientApplication.setActiveAccount(localAccount);
          await setUserProfile(publicClientApplication, authStateUpdate, setGlobalDataCache);
        } else if (allowInteraction) {
          await login(publicClientApplication, entraTenantId, authStateUpdate, setGlobalDataCache);
        } else {
          //nobody can be chosen for the person: the welcome screen asks
          authStateUpdate(false, CurrentUser.getEmptyUser(), false, undefined);
        }
      }
    } else {
      try {
        const ssoRequest: SsoSilentRequest = {
          authority: `https://login.microsoftonline.com/common`,
          scopes: loginRequest.scopes,
          loginHint: getUserNameFromStorage(),
          redirectUri: Config.getAppURL() + '/blank.html',
        };
        logger.debug('No account found, performing SSO silent', ssoRequest);
        const authResult = await publicClientApplication.ssoSilent(ssoRequest);
        if (authResult.account) {
          logger.debug('Account found', authResult.account);
          publicClientApplication.setActiveAccount(authResult.account);
          await setUserProfile(publicClientApplication, authStateUpdate, setGlobalDataCache);
        } else {
          throw new AppError('interaction_required');
        }
      } catch (err) {
        logger.debug('SSO Silent error', err);
        if (isAuthInteractionError(err as Error)) {
          if (allowInteraction) {
            await login(publicClientApplication, entraTenantId, authStateUpdate, setGlobalDataCache);
          } else {
            //no Microsoft session in this browser and the stage has other providers too: the welcome screen
            //asks instead of a popup that presumes Microsoft
            authStateUpdate(false, CurrentUser.getEmptyUser(), false, undefined);
          }
        } else {
          authStateUpdate(false, CurrentUser.getEmptyUser(), false, isAuthUserError(err as Error) ? undefined : err);
        }
      }
    }
  } catch (err) {
    logger.debug('Error', err);
    authStateUpdate(false, CurrentUser.getEmptyUser(), false, isAuthUserError(err as Error) ? undefined : err);
  }
};

const handleRedirectResponse = async (
  publicClientApplication: PublicClientApplication,
  authStateUpdate: AuthStateUpdate,
  setGlobalDataCache: SetGlobalDataCache,
): Promise<boolean> => {
  try {
    //handle redirect responses
    //tokens will be placed into MSAL cache
    //this method will throw on errors (e.g. cancel)
    logger.debug('Start handleRedirectPromise');

    const handleRedirectResult = await publicClientApplication.handleRedirectPromise();

    if (handleRedirectResult) {
      logger.debug('Login account found in handleRedirectPromise', handleRedirectResult.account);
      publicClientApplication.setActiveAccount(handleRedirectResult.account);
      await setUserProfile(publicClientApplication, authStateUpdate, setGlobalDataCache);

      return true;
    }
  } catch (err) {
    logger.debug('handleRedirectPromise', err);
  }

  return false;
};

//
// Normal login
//
const login = async (
  publicClientApplication: PublicClientApplication,
  entraTenantId: string | undefined,
  authStateUpdate: AuthStateUpdate,
  setGlobalDataCache: SetGlobalDataCache,
) => {
  try {
    authStateUpdate(undefined, undefined, true, undefined);
    logger.debug('Method: login');

    const popupRequest: PopupRequest = {
      authority: `https://login.microsoftonline.com/${entraTenantId ? entraTenantId : 'common'}`,
      scopes: loginRequest.scopes,
      loginHint: getUserNameFromStorage(),
      prompt: 'select_account',
      redirectUri: Config.getAppURL() + '/blank.html',
    };

    try {
      await completeLoginPopup(publicClientApplication, popupRequest, authStateUpdate, setGlobalDataCache);
    } catch (err) {
      logger.debug('Login popup error', err);
      if (isPopupWindowError(err)) {
        logger.debug('Popup window error');
        const redirectRequest: RedirectRequest = {
          authority: `https://login.microsoftonline.com/${entraTenantId ? entraTenantId : 'common'}`,
          scopes: loginRequest.scopes,
          loginHint: getUserNameFromStorage(),
          prompt: 'select_account',
          redirectUri: GetValidReturnUrl(),
        };
        logger.debug('Performing redirect login');
        await publicClientApplication.loginRedirect(redirectRequest);

        return;
      }

      if (isConsentRaceError(err as Error)) {
        // Transient consent race - retry login once
        logger.debug('Consent race (AADSTS650051): retrying login once');
        await completeLoginPopup(publicClientApplication, popupRequest, authStateUpdate, setGlobalDataCache);

        return;
      }

      throw err;
    }
  } catch (err) {
    logger.debug('Error', err);
    authStateUpdate(false, CurrentUser.getEmptyUser(), false, isAuthUserError(err as Error) ? undefined : err);
  }
};

const completeLoginPopup = async (
  publicClientApplication: PublicClientApplication,
  popupRequest: PopupRequest,
  authStateUpdate: AuthStateUpdate,
  setGlobalDataCache: SetGlobalDataCache,
) => {
  const authResult: AuthenticationResult = await publicClientApplication.loginPopup(popupRequest);

  logger.debug('Login account', authResult.account);
  publicClientApplication.setActiveAccount(authResult.account);
  await setUserProfile(publicClientApplication, authStateUpdate, setGlobalDataCache);
};

//
// Logout
//
const logout = async (publicClientApplication: PublicClientApplication, authStateUpdate: AuthStateUpdate) => {
  try {
    try {
      await publicClientApplication.logoutPopup();
      authStateUpdate(false, CurrentUser.getEmptyUser(), false, undefined);
    } catch (err) {
      if (isPopupWindowError(err)) {
        const endSessionRequest: EndSessionRequest = {
          postLogoutRedirectUri: Config.getAppURL(),
        };
        await publicClientApplication.logoutRedirect(endSessionRequest);
      }

      throw err;
    }
  } catch {
    authStateUpdate(false, CurrentUser.getEmptyUser(), false, undefined);
  } finally {
    removeLocalStorageData(undefined, LocalStorageKeys.UserName);
    removeLocalStorageData(undefined, LocalStorageKeys.LocalAccount);
  }
};

//
// Switch tenant
//
const switchTenant = async (
  publicClientApplication: PublicClientApplication,
  authStateUpdate: AuthStateUpdate,
  setGlobalDataCache: SetGlobalDataCache,
  tenant: Tenant,
) => {
  try {
    authStateUpdate(undefined, undefined, true, undefined);
    logger.debug(`Method: switchTenant: ${tenant.name}`);
    logger.debug(`Tenant Id: ${tenant.id}`);
    logger.debug(`Tenant Azure Id: ${tenant.providerTenantId}`);

    const accounts = publicClientApplication.getAllAccounts();
    const accountsForTenant = accounts.filter((a) => a.tenantId === tenant.providerTenantId);
    let account: AccountInfo | undefined = undefined;

    if (accountsForTenant.length === 1) {
      logger.debug('Found 1 active account');
      account = accountsForTenant[0];
    } else if (accountsForTenant.length > 1) {
      logger.debug('Found multiple active accounts');
      const currentUserName = publicClientApplication.getActiveAccount()?.username;
      logger.debug('Current user name: ' + (currentUserName ?? 'unknown'));
      if (currentUserName) {
        account = accountsForTenant.find((a) => a.username === currentUserName);
        if (account) logger.debug('Found active account based on user name');
      }
    }

    logger.debug(`Getting MSAL public app for tenant: ${tenant.providerTenantId} ${tenant.name}`);
    publicClientApplication = new PublicClientApplication(getMsalConfigForTenant(tenant));
    await publicClientApplication.initialize();
    let ssoRequest: SsoSilentRequest;

    if (account) {
      ssoRequest = {
        authority: `https://login.microsoftonline.com/${tenant.providerTenantId}`,
        scopes: loginRequest.scopes,
        account: account,
        redirectUri: Config.getAppURL() + '/blank.html',
      };
    } else {
      ssoRequest = {
        authority: `https://login.microsoftonline.com/${tenant.providerTenantId}`,
        scopes: loginRequest.scopes,
        loginHint: getUserNameFromStorage(),
        domainHint: undefined,
        redirectUri: Config.getAppURL() + '/blank.html',
      };
    }

    try {
      logger.debug('Performing SSO silent', ssoRequest);
      const response = await publicClientApplication.ssoSilent(ssoRequest);
      logger.debug('Setting active account', response.account);
      publicClientApplication.setActiveAccount(response.account);
      await setUserProfile(publicClientApplication, authStateUpdate, setGlobalDataCache);
    } catch (err) {
      logger.debug('Error in SSO silent', err);
      if (isAuthInteractionError(err as Error)) {
        logger.debug('Interaction error, performing login', err);
        await login(publicClientApplication, tenant.providerTenantId, authStateUpdate, setGlobalDataCache);
      } else {
        logger.debug('No interaction error, aborting');
        authStateUpdate(false, CurrentUser.getEmptyUser(), false, isAuthUserError(err as Error) ? undefined : err);
      }
    }
  } catch (err) {
    logger.debug('Error', err);
    authStateUpdate(false, CurrentUser.getEmptyUser(), false, isAuthUserError(err as Error) ? undefined : err);
  }
};

//
// admin consent
//
const adminConsentScope = (
  clientId: string,
  scopes: string,
  publicClientApplication: PublicClientApplication,
  authStateUpdate: AuthStateUpdate,
  redirectUrl: string,
  stateSuffix?: string,
) => {
  try {
    // if you want to work with multiple accounts, add your account selection logic below
    const account = publicClientApplication.getActiveAccount();

    if (account) {
      let stateStr = (Math.floor(Math.random() * 90000) + 10000).toString(); // state parameter for anti token forgery
      if (stateSuffix) stateStr += '@' + stateSuffix; //allow the caller to add suffix to state e.g. for including a location hint
      AdminConsentParams.setToLocalStorage(clientId, account.tenantId, stateStr, redirectUrl);
      const adminConsentUri = `https://login.microsoftonline.com/${account.tenantId}/v2.0/adminconsent?client_id=${clientId}&scope=${scopes}&state=${stateStr}&redirect_uri=${msalConfigCommon.auth.redirectUri}/${redirectUrl}`;
      window.location.replace(adminConsentUri);
    } else {
      logger.debug('No active account for admin consent scope');
    }
  } catch (err) {
    authStateUpdate(undefined, undefined, undefined, err);
  }
};

//
// Has scopes
//
const hasScopes = async (
  publicClientApplication: PublicClientApplication,
  scopes: string[],
  azureTenant?: Tenant,
): Promise<boolean> => {
  //
  // Returns true when obtaining an access token silently succeeds, otherwise false
  //
  logger.debug('Method hasScopes: Requested access token check for scopes: ' + scopes.toString());
  let account = publicClientApplication.getActiveAccount() || undefined;

  try {
    if (!account) throw new Error('login_required'); //no AppError to check if this is an auth error
    const publicClientApplicationForAccount = publicClientApplication;

    if (azureTenant && azureTenant.providerTenantId !== account.tenantId) {
      logger.debug('Requested other tenant:', azureTenant);
      //Check if there is an account already for the requested tenant, if so try to get a token with that account
      const accounts = publicClientApplication.getAllAccounts();
      account = accounts.find((a) => a.tenantId === azureTenant.providerTenantId);

      if (!account) {
        return false;
      }
    }

    // Get the access token silently
    // If the cache contains a non-expired token, this function
    // will just return the cached token. Otherwise, it will
    // make a request to the Azure OAuth endpoint to get a token
    const silentResult = await publicClientApplicationForAccount.acquireTokenSilent({
      scopes: scopes,
      account: account,
      authority: `https://login.microsoftonline.com/${account.tenantId}`,
    });

    if (silentResult.accessToken) {
      if (silentResult.tenantId !== account.tenantId) {
        throw new AppError('The selected account is invalid', 'invalid_account');
      }

      logger.debug('Token successfully aquired');

      return true;
    } else {
      throw new AppError('The authorization token could not be obtained', 'invalid_token');
    }
  } catch (err) {
    //in the case that an AppError was already thrown, this means that we should not process the error but re-throw
    //can be the case when getTokenByInteraction throws
    if (err instanceof AppError) {
      throw err;
    }

    // If a silent request fails, it may be because the user needs
    // to login or grant consent to one or more of the requested scopes
    if (isAuthInteractionError(err as Error)) {
      return false;
    }

    logger.debug('Error no interaction', err);
    const appError = AppError.fromAuthError(err);
    throw appError;
  }
};

//
// Get access token
//
const getAccessToken = async (
  publicClientApplication: PublicClientApplication,
  scopes: string[],
  azureTenant?: Tenant,
  allowInteraction: boolean = true,
): Promise<string> => {
  //
  // 1. returns an access token silently
  // 2. when this fails, try to login with popup and obtain token directly
  // 3. when popups are blocked, try to get the token with a redirect and return an empty string
  // 4. throw on any other condition (like cancelling the login)
  //
  logger.debug(`Method getAccessToken: Requested access token for scopes: ${scopes}`);
  let account = publicClientApplication.getActiveAccount() || undefined;
  logger.debug(`Active account tenant: ${account?.tenantId} user: ${account?.username}`);

  try {
    if (!account) throw new Error('login_required'); //no AppError to check if this is an auth error
    logger.debug(`User: ${account.username} Tenant: ${account.tenantId}`);
    const publicClientApplicationForAccount = publicClientApplication;

    if (azureTenant && azureTenant.providerTenantId !== account.tenantId) {
      logger.debug('Requested other tenant:', azureTenant);
      //Check if there is an account already for the requested tenant, if so try to get a token with that account
      const accounts = publicClientApplication.getAllAccounts();
      account = accounts.find((a) => a.tenantId === azureTenant.providerTenantId);

      if (!account) {
        if (!allowInteraction) {
          throw new AppError('interaction_required', 'interaction_required');
        }
        logger.debug('Account not found: interaction login');
        //when there is no account for the request tenant, login into that tenant to get the account information
        const result = await getTokenByInteraction(publicClientApplication, scopes, azureTenant);
        if (result && result.accessToken) {
          logger.debug('Token successfully aquired');

          return result.accessToken;
        } else {
          return ''; //redirect
        }
      } else {
        logger.debug('Cached account found', account);
      }
    }

    // Get the access token silently
    // If the cache contains a non-expired token, this function
    // will just return the cached token. Otherwise, it will
    // make a request to the Azure OAuth endpoint to get a token
    logger.debug('Get token silently');
    const silentResult = await publicClientApplicationForAccount.acquireTokenSilent({
      authority: `https://login.microsoftonline.com/${account.tenantId}`,
      scopes: scopes,
      account: account,
    });

    if (silentResult.accessToken) {
      if (silentResult.tenantId !== account.tenantId) {
        throw new AppError('The selected account is invalid', 'invalid_account');
      }

      logger.debug('Token successfully aquired');

      return silentResult.accessToken;
    } else {
      throw new AppError('The authorization token could not be obtained', 'invalid_token');
    }
  } catch (err) {
    //in the case that an AppError was already thrown, this means that we should not process the error but re-throw
    //can be the case when getTokenByInteraction throws
    if (err instanceof AppError) {
      throw err;
    }

    // If a silent request fails, it may be because the user needs
    // to login or grant consent to one or more of the requested scopes
    if (isAuthInteractionError(err as Error)) {
      logger.debug('Interaction required', err);

      if (!allowInteraction) {
        throw new AppError('interaction_required', 'interaction_required');
      }

      try {
        const result = await getTokenByInteraction(publicClientApplication, scopes, azureTenant);
        if (result && result.accessToken) {
          logger.debug('Token successfully aquired');

          return result.accessToken;
        } else {
          return ''; //redirect
        }
      } catch (err) {
        logger.debug('Error after interaction', err);
        throw err;
      }
    } else {
      logger.debug('Error no interaction', err);
      const appError = AppError.fromAuthError(err);
      throw appError;
    }
  }
};

//
// Get token by interaction
//
const getTokenByInteraction = async (
  publicClientApplication: PublicClientApplication,
  scopes: string[],
  azureTenant?: Tenant,
): Promise<AuthenticationResult | undefined> => {
  //
  //1. try to login with popup
  //2. when this fails due to popup blocker, login with redirect and return undefined to indicate the redirect
  //3. when another error occurs, throw it
  //
  logger.debug('Method: getTokenByInteraction');
  let publicClientApplicationForAccount = publicClientApplication;

  if (azureTenant) {
    logger.debug('Requested specific tenant', azureTenant);
    publicClientApplicationForAccount = new PublicClientApplication(getMsalConfigForTenant(azureTenant));
    await publicClientApplicationForAccount.initialize();
  }

  try {
    //To test redirect, uncomment the following line
    //throw new Error('popup_window_error');
    const account = publicClientApplicationForAccount.getActiveAccount() || undefined;
    logger.debug(`Active account tenant: ${account?.tenantId} user: ${account?.username}`);

    //determine the tenant to use for the authority
    //- when a specific tenant is requested, use that tenant
    //- when no tenant is requested, use the active account tenant
    //- when no active account is found, use common
    const authorityTenant = azureTenant?.providerTenantId || account?.tenantId || 'common';

    const popupRequest: PopupRequest = {
      authority: `https://login.microsoftonline.com/${authorityTenant}`,
      scopes: scopes,
      domainHint: undefined,
      loginHint: getUserNameFromStorage(),
      prompt: 'select_account',
      redirectUri: Config.getAppURL() + '/blank.html',
    };
    const authResult = await publicClientApplicationForAccount.acquireTokenPopup(popupRequest);

    return authResult;
  } catch (err) {
    logger.debug('error aquire token popup', err);
    if (isPopupWindowError(err as Error)) {
      const redirectRequest: RedirectRequest = {
        authority: `https://login.microsoftonline.com/${azureTenant ? azureTenant.providerTenantId : 'common'}`,
        scopes: scopes,
        domainHint: undefined,
        loginHint: getUserNameFromStorage(),
        prompt: 'select_account',
        redirectUri: GetValidReturnUrl(),
      };
      await publicClientApplicationForAccount.acquireTokenRedirect(redirectRequest);

      return undefined;
    }

    const appError = AppError.fromAuthError(err);
    throw appError;
  }
};

//
// User details
//
const setUserProfile = async (
  publicClientApplication: PublicClientApplication,
  authStateUpdate: AuthStateUpdate,
  setGlobalDataCache: SetGlobalDataCache,
) => {
  try {
    logger.debug('Method: Setting user profile');

    saveLocalAccountToStorage(publicClientApplication);
    saveUserNameToStorage(publicClientApplication);

    //Perform login at API
    logger.debug('Getting access token API');
    const accessTokenApi = await getAccessToken(publicClientApplication, apiRequest.scopes);
    logger.debug('Performing login API');
    const login = await apiLoginUser(accessTokenApi);
    if (!login) throw new AppError(`Back-end returned empty login`);

    logger.debug('API login result');
    logger.debug(`userId: ${login.userId}`);
    logger.debug(`tenantId: ${login.tenantId}`);
    logger.debug(`isInTrial: ${login.isInTrial()}`);
    logger.debug(`defLanguageCode: ${login.defLanguageCode}`);
    logger.debug(`tenantLicensed: ${login.tenantLicensed}`);
    logger.debug(`userLicensed: ${login.userLicensed}`);
    logger.debug(`userLanguageCode: ${login.userLanguageCode}`);
    logger.debug('userSystemRoles', login.userSystemRoles);
    logger.debug('tenants', login.tenants);
    logger.debug('isOnboardingInProgress', login.isOnboardingInProgress);

    let currentTenant: Tenant | undefined = undefined;
    let currentUser: User | undefined = undefined;

    if (login.tenantLicensed && login.userLicensed) {
      //When there is a successful login, the tenant and user details are returned
      currentTenant = login.tenants.find((t) => t.id === login.tenantId);
      currentUser = login.globalDataCache.users.items.find((u) => u.id === login.userId);
      if (!currentTenant) throw new AppError('Back-end did not return the current tenant');
      if (!login.userId || !currentUser) throw new AppError('Back-end did not return the current user');
    } else {
      //Otherwise, for example before the organization is created, the back-end does not know this tenant or
      //user: the account of the Microsoft sign-in names them (no Microsoft Graph in this app)
      const account = publicClientApplication.getActiveAccount();
      if (!account) throw new AppError('No active Microsoft account after the sign-in');

      currentTenant = login.tenants.find((t) => t.id === login.tenantId);
      if (!currentTenant) {
        currentTenant = new Tenant(login.tenantId, '');
        currentTenant.identityProviderType = IdentityProviderType.Entra;
        currentTenant.providerTenantId = account.tenantId;
      }

      currentUser = new User(login.userId ?? '', account.username, account.name ?? account.username);
      currentUser.identityProviderType = IdentityProviderType.Entra;
      currentUser.providerUserId = account.localAccountId;
    }

    //the rest of the sign-in is the same for every identity provider; Microsoft Graph is not used for the photo
    await completeLoginProfile(
      login,
      currentTenant,
      currentUser,
      accessTokenApi,
      async () => undefined,
      authStateUpdate,
      setGlobalDataCache,
    );
  } catch (err) {
    logger.debug('Error', err);
    //a cancelled or in-progress interaction is the person's own doing, not an error to show
    authStateUpdate(false, CurrentUser.getEmptyUser(), false, isAuthUserError(err as Error) ? undefined : err);
  }
};

//
// Helpers
//
const GetValidReturnUrl = (): string => {
  try {
    const path = window.location.pathname.toLowerCase();
    if (globalReturnUrls.includes(path)) {
      return window.location.origin + path;
    } else {
      return window.location.origin + '/';
    }
  } catch {
    return window.location.origin + '/';
  }
};

const saveUserNameToStorage = (publicClientApplication: PublicClientApplication) => {
  try {
    const account = publicClientApplication.getActiveAccount();
    if (account) {
      const preferredUserName: string | undefined = account.idTokenClaims?.preferred_username;
      logger.debug('Saving local preferred user name', preferredUserName || 'unknown');
      if (preferredUserName) {
        setLocalStorageData(undefined, LocalStorageKeys.UserName, preferredUserName);
      }
    }
  } catch (err) {
    logger.debug('Cannot save user name to storage', err);
  }
};

const saveLocalAccountToStorage = (publicClientApplication: PublicClientApplication) => {
  try {
    const account = publicClientApplication.getActiveAccount();
    if (account) {
      setLocalStorageData(undefined, LocalStorageKeys.LocalAccount, account.localAccountId);
    }
  } catch (err) {
    logger.debug('Cannot save local account to storage', err);
  }
};

const getUserNameFromStorage = (): string | undefined => {
  const preferredUserName: string | undefined = getLocalStorageData(undefined, LocalStorageKeys.UserName);
  logger.debug('Get local preferred user name', preferredUserName || 'unknown');

  return preferredUserName;
};

const getLocalAccountFromStorage = (): string => {
  return getLocalStorageData(undefined, LocalStorageKeys.LocalAccount) || '';
};

// Export
const authService = {
  hasScopes: hasScopes,
  getAccessToken: getAccessToken,
  handleRedirectResponse: handleRedirectResponse,
  ssoSilent: ssoSilent,
  login: login,
  logout: logout,
  setUserProfile: setUserProfile,
  adminConsentScope: adminConsentScope,
  switchTenant: switchTenant,
};

export default authService;
